// client/src/dnd/emitter_tool/runtime/UnifiedRuntime.js
import * as PIXI from "pixi.js";
import GlowManager from "../engine/GlowManager.js";
import { createGlowRegistry } from "./Runtime_Parts/GlowRegistry.js";
import { getRootContainer } from "./Runtime_Parts/fogHelpers.js";
import { createSimpleEmitter, createCollisionManager } from "./Runtime_Parts/SimpleEmitter.js";
import { resolveSpriteSource, clone, deepMerge, toNum } from "./Runtime_Parts/utils.js";
import { applyOrbitToEmitter } from "../engine/OrbitManager.js";
import { applyRotationToEmitter } from "../engine/RotationManager.js";
import { createSubEmitterManager } from "../engine/SubEmitterManager.js";
import GravityManager from "../engine/GravityManager.js";
import { createTrailManager } from "../engine/TrailManager.js";

const UI_FIRE_MODE_KEY = "EmitterTool:UI:fireMode";
const UI_RULE_STYLE_KEY = "EmitterTool:UI:ruleStyle";
const UI_RULE_DUR_KEY = "EmitterTool:UI:ruleDur";
const UI_PICKED_X_KEY = "EmitterTool:UI:pickedOriginX";
const UI_PICKED_Y_KEY = "EmitterTool:UI:pickedOriginY";
const UI_TTT_SPEED_MPS = "EmitterTool:UI:tokenToTargetSpeedMps";
const UI_TIMED_DUR_KEY = "EmitterTool:UI:timedDur";
const UI_AMBIENT_LOCK = "EmitterTool:UI:ambientLock";

const ensureArray = (a) => (Array.isArray(a) ? a : []);
const DEG = Math.PI / 180;

const ENGINE_OVERLAY_ENABLED = false;

function mergeIntoDeep(target, src) {
  if (!src || typeof src !== "object") return target;
  for (const key of Object.keys(src)) {
    const sv = src[key];
    const tv = target[key];
    if (sv && typeof sv === "object" && !Array.isArray(sv)) {
      if (!tv || typeof tv !== "object" || Array.isArray(tv)) target[key] = {};
      mergeIntoDeep(target[key], sv);
    } else {
      target[key] = sv;
    }
  }
  return target;
}

const SPRITE_KEYS = new Set(["selectedSprite", "selectedSprites", "selectedAnimFrames", "animFps"]);
function stripSpriteKeys(obj) {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  for (const k of Object.keys(obj)) if (!SPRITE_KEYS.has(k)) out[k] = obj[k];
  return out;
}

export function createUnifiedEmitterRuntime(arg = {}) {
  const PIXI_ = arg.PIXIRef || PIXI;
  const appRef = arg.app || null;
  const autorun = arg.autorun !== false;

  let spriteManager = arg.spriteManager || null;
  let texturesRef = arg.textures || {};
  const parentLayerArg = arg.parentLayer || null;
  const aboveLayerArg = arg.aboveLayer || null;
  const getPxPerMeter = typeof arg.getPxPerMeter === "function" ? arg.getPxPerMeter : null;
  const staticPPM = Number.isFinite(arg.pxPerMeter) ? Number(arg.pxPerMeter) : null;

  const usePresetRulesOnly = !!arg.usePresetRulesOnly;

  let atlas = null;
  const live = new Set();
  let lastRunArgs = null;

  let activeScope = { type: "parent", id: "parent" };
  const particlesPatchStore = { parent: {}, children: new Map() };
  let particlesPatchVersion = 0;

  let sessionPick = null;
  let fireCounter = 0;
  let phaseActive = false;

  let ruleStyle = (() => {
    if (usePresetRulesOnly) return "infinite";
    try {
      return (localStorage.getItem(UI_RULE_STYLE_KEY) || "infinite").toLowerCase();
    } catch {
      return "infinite";
    }
  })();

  let ruleDurationS = (() => {
    if (usePresetRulesOnly) return 2;
    try {
      return Math.max(0, toNum(localStorage.getItem(UI_RULE_DUR_KEY), 2));
    } catch {
      return 2;
    }
  })();

  let fixedMode = null;

  let currentModeCache = null;
  let lastRestartTime = 0;
  const restartCooldownMs = 250;
  let gravityAmbientActive = false;
  let timedArmed = false;
  let timedRemainS = 0;

  const subRuleOverrides = new Map();
  try {
    window.addEventListener("EmitterTool:SubEmitterRule", (e) => {
      const d = e?.detail || {};
      const id = d.id;
      if (!id) return;
      const rule = {
        startMode: d.startMode || "immediate",
        delayMs: Number(d.delayMs) || 0,
        runForMode: d.runForMode || "inherit",
        runDurationS: Number(d.runDurationS) || 0.5,
        enabled: d.enabled !== false,
      };
      subRuleOverrides.set(id, rule);
    });
  } catch {}

  function nowMs() {
    return (typeof performance !== "undefined" && performance.now)
      ? performance.now()
      : Date.now();
  }

  function resolvePxPerMeter(fallback = 80) {
    const v = getPxPerMeter ? Number(getPxPerMeter()) : NaN;
    if (Number.isFinite(v) && v > 0) return v;
    if (Number.isFinite(staticPPM) && v !== 0 && staticPPM > 0) return staticPPM;
    return fallback;
  }

  function isAmbientLocked() {
    try {
      if (typeof window !== "undefined" && window.__ET_INPUT_DISABLED) return true;
    } catch {}
    try {
      const v = localStorage.getItem(UI_AMBIENT_LOCK);
      if (v && v !== "0" && v !== "false") return true;
    } catch {}
    return false;
  }

  const onRuleCfg = (e) => {
    const d = e?.detail || {};
    const style = d?.ruleStyle ?? d?.style;
    const dur = d?.ruleDurationS ?? d?.durationS ?? d?.ruleDur ?? d?.duration;
    if (!usePresetRulesOnly) {
      if (style) ruleStyle = String(style).toLowerCase();
      if (Number.isFinite(+dur)) ruleDurationS = Math.max(0, +dur);
    }
  };

  try {
    window.addEventListener("EmitterTool:RuleConfig", onRuleCfg);
  } catch {}
  try {
    window.addEventListener("EmitterTool:UI:RuleConfig", onRuleCfg);
  } catch {}

  try {
    window.addEventListener("EmitterTool:ScopeChanged", (e) => {
      const d = e?.detail || {};
      const t = String(d.scopeType || d.type || "").toLowerCase();
      const id = d.scopeId || d.id || (t === "parent" ? "parent" : undefined);
      if (t === "parent") activeScope = { type: "parent", id: "parent" };
      else if (t === "child" && id) activeScope = { type: "child", id: String(id) };
    });
  } catch {}

  const onParticlesCfg = (e) => {
    const d = e?.detail || {};
    let t = String(d.scopeType || d.type || "").toLowerCase();
    let sid = d.scopeId || d.id;
    if (!t) t = activeScope.type;
    if (!sid) sid = activeScope.id;
    const patch = stripSpriteKeys(d || {});
    delete patch.scopeType;
    delete patch.scopeId;
    delete patch.type;
    delete patch.id;
    if (t === "child" && sid) {
      const id = String(sid);
      const cur = particlesPatchStore.children.get(id) || {};
      particlesPatchStore.children.set(id, mergeIntoDeep(cur, patch));
    } else {
      particlesPatchStore.parent = mergeIntoDeep(particlesPatchStore.parent || {}, patch);
    }
    particlesPatchVersion++;
  };

  try {
    window.addEventListener("EmitterTool:ParticlesConfig", onParticlesCfg);
  } catch {}

  function clearAllInstances() {
    for (const inst of live) {
      try {
        inst.destroy?.();
      } catch {}
    }
    live.clear();
  }

  function hasLive() {
    return live.size > 0;
  }

  function restartIfPossible() {
    if (gravityAmbientActive || isAmbientLocked() || hasLive()) return;
    const now = nowMs();
    if (now - lastRestartTime < restartCooldownMs) return;
    lastRestartTime = now;

    let modeLS = "infinite";
    try {
      modeLS = (localStorage.getItem(UI_FIRE_MODE_KEY) || "infinite").toLowerCase();
    } catch {}

    if (currentModeCache === modeLS && hasLive()) return;

    currentModeCache = modeLS;
    if (modeLS === "off") {
      clearAllInstances();
      return;
    }
    if (!lastRunArgs) return;

    const { preset, fromImg, parentLayer, aboveLayer, helpers } = lastRunArgs;
    clearAllInstances();
    runPreset(preset, fromImg, parentLayer, aboveLayer, helpers);
  }

  function setTextures(next) {
    texturesRef = next || {};
    atlas = { textures: texturesRef, frameNames: Object.keys(texturesRef), spritesheet: null };
    if (spriteManager?.setTextures) {
      try {
        spriteManager.setTextures(texturesRef);
      } catch {}
    }
  }

  function setSpriteManager(sm) {
    spriteManager = sm || null;
    if (spriteManager?.setTextures) {
      try {
        spriteManager.setTextures(texturesRef);
      } catch {}
    }
  }

  const glowReg = createGlowRegistry({
    appRef,
    pxPerMeterProvider: () => resolvePxPerMeter(80),
  });

  let _glowWireCooldown = 0;

  let glowEnv = {};

  const uiHandlersInstalled = { v: false };

  let overlayPresetId = "direction";
  let overlayRangeM = 9;
  let overlaySpreadDeg = 60;
  let lastOverlayEmitT = 0;
  const OVERLAY_MIN_DT_MS = 80;

  function emitOverlayAngle(angleDeg) {
    if (!ENGINE_OVERLAY_ENABLED) return;

    const now = nowMs();
    if (now - lastOverlayEmitT < OVERLAY_MIN_DT_MS) return;
    lastOverlayEmitT = now;
    if (!angleDeg && angleDeg !== 0) return;

    let shape = "line";
    let settings = {};

    if (overlayPresetId === "global") {
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", {
            detail: { show: false },
          })
        );
      } catch {}
      return;
    } else if (overlayPresetId === "direction") {
      shape = "cone";
      settings = { spreadDeg: overlaySpreadDeg, angleDeg, centerLocked: true };
    } else {
      shape = "line";
      settings = { height: 0, angleDeg, centerLocked: true };
    }

    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool{EIFControlEvent", {
          detail: { show: true, shape, settings },
        })
      );
    } catch {}
  }

  function onUIPreset(e) {
    const p = e?.detail || {};
    const id = String(p.presetId || "").toLowerCase();
    if (id) overlayPresetId = id;
    const tg = p.spell?.targeting || {};
    const a = Number(tg.angleDeg);
    if (Number.isFinite(a)) overlaySpreadDeg = Math.max(0, a);
  }

  function onUIRange(e) {
    const v = Number(e?.detail?.rangeM);
    if (Number.isFinite(v)) overlayRangeM = Math.max(0, v);
  }

  try {
    window.addEventListener("EmitterTool:UI:TargetingPreset", onUIPreset);
  } catch {}
  try {
    window.addEventListener("EmitterTool:Targeting:SetRangeM", onUIRange);
  } catch {}

  function installUIHandlers() {
    if (uiHandlersInstalled.v) return;
    uiHandlersInstalled.v = true;

    const onStop = () => {
      if (isAmbientLocked() && gravityAmbientActive) return;
      phaseActive = false;
      try {
        window.dispatchEvent(new CustomEvent("EmitterTool:RulePhaseEnd"));
      } catch {}
    };

    const onModeChange = () => {
      if (hasLive()) return;
      restartIfPossible();
    };

    const onPowerOn = () => {
      if (hasLive()) return;
      restartIfPossible();
    };

    const onInfiniteStart = () => {
      if (hasLive()) return;
      restartIfPossible();
    };

    const onTimedStart = () => {
      timedArmed = false;
      if (hasLive()) return;
      restartIfPossible();
    };

    const onPicked = (e) => {
      const d = e?.detail || {};
      const a = d?.angleDeg;
      const angleDeg = Number.isFinite(+a) ? +a : undefined;
      const devX = Number(d?.devX);
      const devY = Number(d?.devY);
      const dpr = Number(d?.dpr) || (window?.devicePixelRatio || 1);
      const xCss = Number(d?.x);
      const yCss = Number(d?.y);
      const tCssX = Number(d?.targetXCss);
      const tCssY = Number(d?.targetYCss);
      const headSpeedMps = Number(d?.headSpeedMps);

      if (Number.isFinite(devX) && Number.isFinite(devY)) {
        sessionPick = {
          devX,
          devY,
          dpr,
          xCss: Number.isFinite(xCss) ? xCss : undefined,
          yCss: Number.isFinite(yCss) ? yCss : undefined,
          angleDeg,
        };
      } else if (Number.isFinite(xCss) && Number.isFinite(yCss)) {
        sessionPick = {
          devX: xCss * dpr,
          devY: yCss * dpr,
          dpr,
          xCss,
          yCss,
          angleDeg,
        };
      }

      if (sessionPick && Number.isFinite(tCssX) && Number.isFinite(tCssY)) {
        sessionPick.targetDevX = tCssX * dpr;
        sessionPick.targetDevY = tCssY * dpr;
        sessionPick.targetXCss = tCssX;
        sessionPick.targetYCss = tCssY;
      }

      if (sessionPick && Number.isFinite(headSpeedMps)) {
        sessionPick.headSpeedMps = headSpeedMps;
      }

      if (Number.isFinite(angleDeg)) {
        emitOverlayAngle(angleDeg);
      } else if (
        Number.isFinite(sessionPick?.xCss) &&
        Number.isFinite(sessionPick?.yCss) &&
        Number.isFinite(sessionPick?.targetXCss) &&
        Number.isFinite(sessionPick?.targetYCss)
      ) {
        const dx = sessionPick.targetXCss - sessionPick.xCss;
        const dy = sessionPick.targetYCss - sessionPick.yCss;
        const ang = (Math.atan2(dy, dx) * 180) / Math.PI;
        emitOverlayAngle(((ang % 360) + 360) % 360);
      }
    };

    function ensurePickFromStorage() {
      if (sessionPick && Number.isFinite(sessionPick.devX) && Number.isFinite(sessionPick.devY))
        return true;
      const xCss = Number(localStorage.getItem(UI_PICKED_X_KEY));
      const yCss = Number(localStorage.getItem(UI_PICKED_Y_KEY));
      const dpr = window?.devicePixelRatio || 1;
      if (Number.isFinite(xCss) && Number.isFinite(yCss)) {
        sessionPick = { devX: xCss * dpr, devY: yCss * dpr, dpr, xCss, yCss };
        return true;
      }
      return false;
    }

    const onFire = () => {
      if (isAmbientLocked() && gravityAmbientActive) return;
      if (phaseActive) return;
      fireCounter++;
      ensurePickFromStorage();
      phaseActive = true;
      try {
        window.dispatchEvent(new CustomEvent("EmitterTool:RulePhaseStart"));
      } catch {}
    };

    window.addEventListener("EmitterTool:PowerOff", onStop);
    window.addEventListener("EmitterTool:FireStop", onStop);
    window.addEventListener("EmitterTool:PowerOn", onPowerOn);
    window.addEventListener("EmitterTool:InfiniteStart", onInfiniteStart);
    window.addEventListener("EmitterTool:TimedStart", onTimedStart);
    window.addEventListener("EmitterTool:FireRule", onFire);
    window.addEventListener("EmitterTool:UI:FireRule", onFire);
    window.addEventListener("EmitterTool:ModeChange", onModeChange);
    window.addEventListener("EmitterTool:UI:PickedLocation", onPicked);
    window.addEventListener("EmitterTool:PickedLocation", onPicked);
    window.addEventListener("EmitterTool:RuleConfig", onRuleCfg);
    window.addEventListener("EmitterTool:UI:RuleConfig", onRuleCfg);
    window.addEventListener("EmitterTool:StopRule", () => {
      if (isAmbientLocked() && gravityAmbientActive) return;
      phaseActive = false;
      sessionPick = null;
      try {
        window.dispatchEvent(new CustomEvent("EmitterTool:RulePhaseEnd"));
      } catch {}
    });
  }

  function gravityLockedOrigin(emitterShape, imageRect, fallback) {
    const s = String(emitterShape || "").toLowerCase();
    if (
      s === "random" ||
      s === "full" ||
      s === "fullcontainer" ||
      s === "container" ||
      s === "screen" ||
      s === "image" ||
      s === "canvas"
    ) {
      return { x: imageRect.width * 0.5, y: imageRect.height * 0.5 };
    }
    return fallback;
  }

  function textureExists(name) {
    if (!name) return false;
    try {
      return !!(
        atlas?.textures &&
        Object.prototype.hasOwnProperty.call(atlas.textures, name)
      );
    } catch {
      return false;
    }
  }

  function selectionFromEffectOrOverrides(effect, overrides, fallbackSel) {
    const ov = overrides || {};
    const eff = effect || {};

    if (ov.selectedSprite && textureExists(ov.selectedSprite)) {
      return {
        mode: "single",
        single: ov.selectedSprite,
        multi: [],
        anim: [],
        animFps: toNum(ov.animFps, 12) || 12,
      };
    }
    if (Array.isArray(ov.selectedSprites) && ov.selectedSprites.length) {
      const ok = ov.selectedSprites.filter(textureExists);
      if (ok.length === 1)
        return {
          mode: "single",
          single: ok[0],
          multi: [],
          anim: [],
          animFps: toNum(ov.animFps, 12) || 12,
        };
      if (ok.length > 1)
        return {
          mode: "multi",
          single: null,
          multi: ok,
          anim: [],
          animFps: toNum(ov.animFps, 12) || 12,
        };
    }
    if (Array.isArray(ov.selectedAnimFrames) && ov.selectedAnimFrames.length >= 2) {
      const ok = ov.selectedAnimFrames.filter(textureExists);
      if (ok.length >= 2)
        return {
          mode: "anim",
          single: null,
          multi: [],
          anim: ok,
          animFps: toNum(ov.animFps, 12) || 12,
        };
    }

    if (eff.selectedSprite && textureExists(eff.selectedSprite)) {
      return {
        mode: "single",
        single: eff.selectedSprite,
        multi: [],
        anim: [],
        animFps: toNum(eff.animFps, 12) || 12,
      };
    }
    if (Array.isArray(eff.selectedSprites) && eff.selectedSprites.length) {
      const ok = eff.selectedSprites.filter(textureExists);
      if (ok.length === 1)
        return {
          mode: "single",
          single: ok[0],
          multi: [],
          anim: [],
          animFps: toNum(eff.animFps, 12) || 12,
        };
      if (ok.length > 1)
        return {
          mode: "multi",
          single: null,
          multi: ok,
          anim: [],
          animFps: toNum(eff.animFps, 12) || 12,
        };
    }
    if (Array.isArray(eff.selectedAnimFrames) && eff.selectedAnimFrames.length >= 2) {
      const ok = eff.selectedAnimFrames.filter(textureExists);
      if (ok.length >= 2)
        return {
          mode: "anim",
          single: null,
          multi: [],
          anim: ok,
          animFps: toNum(eff.animFps, 12) || 12,
        };
    }

    return fallbackSel;
  }

  function normalizeStartMode(v) {
    const s = String(v || "").toLowerCase();
    if (s === "delay" || s === "wait" || s === "wait_ms" || s === "waitms") return "delay";
    if (s === "after" || s === "afterparent" || s === "after-parent" || s === "after_parent" || s === "onparentend" || s === "end") return "afterParent";
    if (s === "immediate" || s === "now" || s === "instant") return "immediate";
    return "immediate";
  }
  function pickDelayMs(obj) {
    const c = obj || {};
    const raw =
      c.delayMs ?? c.delayMS ?? c.delay_ms ??
      c.delay ?? c.wait ?? c.waitMs ?? c.wait_ms ?? 0;
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  }
  function normalizeRunForMode(v) {
    const s = String(v || "").toLowerCase();
    if (s === "timed" || s === "time" || s === "duration") return "timed";
    return "inherit";
  }

  function buildChildLayersForRuntime(presetObj) {
    const rawLayers = Array.isArray(presetObj?.layers) ? presetObj.layers : [];
    if (rawLayers.length > 0) {
      return rawLayers
        .map((L) => {
          if (!L) return null;
          const copy = clone(L);
          copy.startMode = normalizeStartMode(copy.startMode);
          copy.delayMs = pickDelayMs(copy);
          copy.runForMode = normalizeRunForMode(copy.runForMode);
          if (!Number.isFinite(copy.runDurationS)) {
            const dAlt = Number(copy.runDuration ?? copy.runForDuration ?? copy.durationS ?? copy.duration);
            copy.runDurationS = Number.isFinite(dAlt) ? dAlt : 0.5;
          }
          return copy;
        })
        .filter((l) => l && l.enabled !== false);
    }

    const subList = ensureArray(presetObj?.parent?.effectSettings?.subEmitters);
    if (!subList.length) return [];

    const mapped = subList
      .filter((c) => c && c.enabled !== false)
      .map((c) => {
        const ov = c.overrides || {};
        return {
          id: c.id || "",
          enabled: c.enabled !== false,
          startMode: normalizeStartMode(c.startMode || "immediate"),
          delayMs: pickDelayMs(c),
          runForMode: normalizeRunForMode(c.runForMode || "inherit"),
          runDurationS: Number(c.runDurationS ?? c.runDuration ?? c.durationS ?? c.duration) || 0.5,
          aboveParent: ov.aboveParent != null ? !!ov.aboveParent : true,
          emitterShape: ov.emitterShape,
          shapeSettings: ov.shapeSettings,
          effectSettings: ov.effectSettings,
          __selectionOverrides: {
            selectedSprite: ov.selectedSprite,
            selectedSprites: ov.selectedSprites,
            selectedAnimFrames: ov.selectedAnimFrames,
            animFps: ov.animFps,
          },
        };
      });

    return mapped;
  }

  async function runPreset(preset, fromImg, parentLayer, aboveLayer, helpers) {
    if (!appRef) return;

    fixedMode = null;

    let helperPxPerMeter = null;
    if (helpers) {
      if (typeof helpers.pxPerMeter === "function") {
        helperPxPerMeter = helpers.pxPerMeter();
      } else if (typeof helpers.mpp === "function") {
        helperPxPerMeter = helpers.mpp();
      } else {
        helperPxPerMeter = helpers.pxPerMeter ?? helpers.mpp ?? null;
      }
    }
    const helperImageSize =
      helpers && typeof helpers.imageSize === "function"
        ? helpers.imageSize()
        : helpers?.imageSize;

    installUIHandlers();
    lastRunArgs = { preset, fromImg, parentLayer, aboveLayer, helpers };

    const appendMode = !!(helpers && helpers.append);

    const parentBlock = clone(preset?.parent || {});
    const parent = {
      emitterShape: parentBlock.emitterShape || "dot",
      shapeSettings: parentBlock.shapeSettings || {},
      effectSettings: { ...(parentBlock.effectSettings || {}) },
    };

    const presetRuleRaw =
      (preset && preset.EmitterPanel && preset.EmitterPanel.rule) ||
      preset?.rule ||
      parent.shapeSettings?.rule ||
      null;

    if (presetRuleRaw && typeof presetRuleRaw === "object") {
      const style =
        presetRuleRaw.ruleStyle ||
        presetRuleRaw.style ||
        presetRuleRaw.mode ||
        null;
      const dur =
        presetRuleRaw.ruleDurationS ??
        presetRuleRaw.durationS ??
        presetRuleRaw.duration ??
        null;
      const fireMode =
        presetRuleRaw.fireMode ||
        presetRuleRaw.fire_mode ||
        null;

      if (style) {
        ruleStyle = String(style).toLowerCase();
      }
      if (dur != null && Number.isFinite(+dur)) {
        ruleDurationS = Math.max(0, +dur);
      }

      if (usePresetRulesOnly) {
        let modeFromPreset = null;

        if (fireMode) {
          const fm = String(fireMode).toLowerCase();
          if (fm === "timed") modeFromPreset = "timed";
          else if (fm === "off") modeFromPreset = "off";
          else if (fm === "rule") {
            modeFromPreset = ruleStyle === "timed" ? "timed" : "infinite";
          } else if (fm === "infinite") {
            modeFromPreset = "infinite";
          }
        }

        if (!modeFromPreset) {
          modeFromPreset = ruleStyle === "timed" ? "timed" : "infinite";
        }

        fixedMode = modeFromPreset;
      }
    } else if (usePresetRulesOnly) {
      fixedMode = "infinite";
    }

    const layers = buildChildLayersForRuntime(preset);

    const presetPPM = Number(preset?.pxPerMeter);
    const helperPPMVal = Number(helpers && (helpers.pxPerMeter ?? helpers.mpp));
    const pxPerMeterVal =
      Number.isFinite(presetPPM) && presetPPM > 0
        ? presetPPM
        : Number.isFinite(helperPPMVal) && helperPPMVal > 0
        ? helperPPMVal
        : (function (fallback = 80) {
            const v = getPxPerMeter ? Number(getPxPerMeter()) : NaN;
            return Number.isFinite(v) && v > 0
              ? v
              : Number.isFinite(staticPPM) && staticPPM > 0
              ? staticPPM
              : fallback;
          })();

    const refStepId =
      (helpers &&
        (helpers.refStepId ??
          helpers.stepRefId ??
          helpers.stepId ??
          (helpers.seq && (helpers.seq.refStepId ?? helpers.seq.stepId)))) ??
      (preset &&
        (preset.refStepId ??
          preset.stepRefId ??
          preset.stepId ??
          (preset.sequencer &&
            (preset.sequencer.refStepId ?? preset.sequencer.stepId)))) ??
      null;

    const root = getRootContainer(appRef);
    const rootW = root?.width || appRef?.renderer?.width || 1280;
    const rootHRaw = root?.height || appRef?.renderer?.height || 720;

    let imgW = 1920;
    let imgH = 1080;
    if (Array.isArray(helperImageSize) && helperImageSize.length >= 2) {
      const hw = Number(helperImageSize[0]);
      const hh = Number(helperImageSize[1]);
      if (Number.isFinite(hw) && hw > 0) imgW = hw;
      if (Number.isFinite(hh) && hh > 0) imgH = hh;
    }

    const imageRect = { x: 0, y: 0, width: imgW, height: imgH };

    const helperOrigin =
      helpers && typeof helpers.getOrigin === "function"
        ? helpers.getOrigin({ imageRect })
        : null;

    let initX = fromImg?.x;
    let initY = fromImg?.y;

    if (!Number.isFinite(initX) && helperOrigin && Number.isFinite(helperOrigin.x)) {
      initX = helperOrigin.x;
    }
    if (!Number.isFinite(initY) && helperOrigin && Number.isFinite(helperOrigin.y)) {
      initY = helperOrigin.y;
    }
    if (!Number.isFinite(initX)) initX = imageRect.width * 0.5;
    if (!Number.isFinite(initY)) initY = imageRect.height * 0.5;

    const parentLayerMount = parentLayer || parentLayerArg || root || appRef?.stage;
    const aboveLayerMount = aboveLayer || aboveLayerArg || root || appRef?.stage;
    try {
      parentLayerMount.sortableChildren = true;
    } catch {}
    try {
      aboveLayerMount.sortableChildren = true;
    } catch {}

    const baseSpriteSource = resolveSpriteSource({
      spriteManager,
      textures: atlas?.textures || {},
    });

    const baseSelection =
      spriteManager && typeof spriteManager.getSelectionForEngine === "function"
        ? spriteManager.getSelectionForEngine()
        : {
            mode: "single",
            single: null,
            multi: [],
            anim: [],
            animFps: 12,
          };

    const collision = createCollisionManager(imageRect, helpers, parent.effectSettings);

    let parentGlow;
    if (usePresetRulesOnly) {
      parentGlow = new GlowManager({
        pxPerMeter: pxPerMeterVal,
        glowEventName: "fx-glow",
      });
      try {
        parentGlow.configure(parent.effectSettings || {}, glowEnv.gridConfig || {});
      } catch {}
    } else {
      parentGlow = glowReg.add(
        new GlowManager(),
        parent.effectSettings || {},
        {
          pxPerMeter: pxPerMeterVal,
          gridConfig: glowEnv.gridConfig || {},
          isVTT: glowEnv.isVTT === true,
        }
      );
    }

    const parentGravity = new GravityManager();

    const emitterGroups = [];
    const makeGroup = (mount, name, z) => {
      const g = new PIXI_.Container();
      try {
        g.sortableChildren = true;
      } catch {}
      if (Number.isFinite(z)) {
        try {
          g.zIndex = z;
        } catch {}
      }
      try {
        g.name = name;
      } catch {}
      try {
        mount.addChild(g);
      } catch {}
      emitterGroups.push(g);
      return g;
    };

    const parentPatch = particlesPatchStore.parent || {};
    const parentES = deepMerge(clone(parent.effectSettings || {}), parentPatch);
    const parentSel = selectionFromEffectOrOverrides(parentES, null, baseSelection);
    const parentGroup = makeGroup(aboveLayerMount, "emitter:parent", 2000);

    let lastDeathPos = null;
    let lastKnownOrigin = null;
    let particlesEmptySent = false;

    const parentEmitter = createSimpleEmitter(appRef, parentGroup, {
      textures: atlas?.textures || {},
      pxPerMeter: pxPerMeterVal,
      emitterShape: parent.emitterShape,
      shapeSettings: parent.shapeSettings,
      effectSettings: parentES,
      spriteSource: baseSpriteSource,
      selection: parentSel,
      imageRect,
      collision,
      glowManager: parentGlow,
      gravityManager: parentGravity,
      refStepId,
      onParticleDeath: (p) => {
        if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
          lastDeathPos = { x: p.x, y: p.y };
        }
      },
    });

    try {
      parentEmitter.setSpawning(false);
    } catch {}

    const sub = createSubEmitterManager({
      PIXI: PIXI_,
      appRef,
      parentLayerMount,
      aboveLayerMount,
      baseSpriteSource,
      baseSelection,
      atlasTextures: atlas?.textures || {},
      imageRect,
      collision,
      pxPerMeter: pxPerMeterVal,
      parentEmitterShape: parent.emitterShape,
      parentShapeSettings: parent.shapeSettings,
      parentEffectSettings: parentES,
      refStepId,
      layers,
    });

    if (!usePresetRulesOnly) {
      try {
        glowReg.wireNow();
      } catch {}
    }

    const trail = createTrailManager({
      PIXI: PIXI_,
      mount: parentGroup,
      getEffectSettings: () => parentES,
      textures: atlas?.textures || {},
      spriteSource: baseSpriteSource,
    });

    const emittersAll = [parentEmitter, ...sub.getChildEmitters()];

    const anyLive = () => emittersAll.some((em) => em.hasLiveParticles && em.hasLiveParticles());

    const resetAllEmitters = (opts) => {
      parentEmitter.reset(opts || {});
      sub.resetChildren(opts);
    };

    const setOriginAll = (p) => {
      if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
      parentEmitter.setOrigin(p);
      sub.setOriginChildren(p);
      lastKnownOrigin = { x: p.x, y: p.y };
    };

    const setRotationDegAll = (deg) => {
      parentEmitter.setRotationDeg(deg);
      sub.setRotationDegChildren(deg);
    };

    const anyGravityEnabled = () => {
      if (parentES?.gravityEnabled) return true;
      return sub.anyChildGravityEnabled();
    };

    gravityAmbientActive = anyGravityEnabled();

    const getDefaultFadeOutS = () => {
      let v = Number(parentES?.fadeOutS);
      if (!Number.isFinite(v) || v <= 0) {
        v = Number(parentES?.fadeOutSeconds);
      }
      if ((!Number.isFinite(v) || v <= 0) && Number.isFinite(parentES?.fadeOutMs)) {
        v = parentES.fadeOutMs / 1000;
      }
      if (!Number.isFinite(v) || v <= 0) v = 3.0;
      return v;
    };

    let forceFadeActive = false;
    let forceFadeTotalS = 0;
    let forceFadeRemainS = 0;

    const startForcedFadeOut = (durOverride) => {
      if (forceFadeActive) return;
      const d = Number(durOverride);
      const base = Number.isFinite(d) && d > 0 ? d : getDefaultFadeOutS();
      forceFadeTotalS = base;
      forceFadeRemainS = base;
      forceFadeActive = true;
    };

    let moveHeadActive = false;
    let headPos = null;
    let headTarget = null;
    let headSpeedPxPerS = 0;

    {
      let oLocal =
        Number.isFinite(initX) && Number.isFinite(initY)
          ? { x: initX, y: initY }
          : { x: imageRect.width * 0.5, y: imageRect.height * 0.5 };

      if (gravityAmbientActive) {
        oLocal = gravityLockedOrigin(parent.emitterShape, imageRect, oLocal);
      }

      setOriginAll(oLocal);
    }

    function configureGravityAll(currentOrigin) {
      try {
        parentGravity.configure({
          emitterShape: parent.emitterShape,
          shapeSettings: parent.shapeSettings,
          effectSettings: parentES,
          origin: currentOrigin,
          pxPerMeter: pxPerMeterVal,
          imageRect,
        });
      } catch {}
      sub.configureGravityChildren(currentOrigin);
    }

    try {
      const seed =
        parentEmitter.getOrigin?.() || {
          x: imageRect.width * 0.5,
          y: imageRect.height * 0.5,
        };
      configureGravityAll(seed);
    } catch {}

    function dispatchRuntimeLastOrigin(reason, originOverride) {
      let o = originOverride;
      if (!o || !Number.isFinite(o.x) || !Number.isFinite(o.y)) {
        if (
          lastDeathPos &&
          Number.isFinite(lastDeathPos.x) &&
          Number.isFinite(lastDeathPos.y)
        ) {
          o = { x: lastDeathPos.x, y: lastDeathPos.y };
        } else if (
          lastKnownOrigin &&
          Number.isFinite(lastKnownOrigin.x) &&
          Number.isFinite(lastKnownOrigin.y)
        ) {
          o = { x: lastKnownOrigin.x, y: lastKnownOrigin.y };
        } else if (parentEmitter.getOrigin) {
          const tmp = parentEmitter.getOrigin();
          if (tmp && Number.isFinite(tmp.x) && Number.isFinite(tmp.y)) {
            o = { x: tmp.x, y: tmp.y };
          }
        }
      }
      if (!o) {
        o = { x: imageRect.width * 0.5, y: imageRect.height * 0.5 };
      }
      lastKnownOrigin = { x: o.x, y: o.y };
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:Runtime:LastOrigin", {
            detail: {
              x: o.x,
              y: o.y,
              pxPerMeter: pxPerMeterVal,
              imageRect,
              refStepId: refStepId ?? undefined,
              reason: reason || "unknown",
            },
          })
        );
      } catch {}
    }

    function dispatchParticlesEmpty(reason) {
      if (particlesEmptySent) return;
      particlesEmptySent = true;
      const detail = {};
      if (
        lastDeathPos &&
        Number.isFinite(lastDeathPos.x) &&
        Number.isFinite(lastDeathPos.y)
      ) {
        detail.deathX = lastDeathPos.x;
        detail.deathY = lastDeathPos.y;
      } else if (
        lastKnownOrigin &&
        Number.isFinite(lastKnownOrigin.x) &&
        Number.isFinite(lastKnownOrigin.y)
      ) {
        detail.deathX = lastKnownOrigin.x;
        detail.deathY = lastKnownOrigin.y;
      }
      detail.pxPerMeter = pxPerMeterVal;
      detail.imageRect = imageRect;
      if (refStepId != null) detail.refStepId = refStepId;
      if (reason) detail.reason = reason;
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:ParticlesEmpty", { detail })
        );
      } catch {}
      if (refStepId != null) {
        try {
          window.dispatchEvent(
            new CustomEvent("EmitterTool:Sequencer:StepComplete", {
              detail,
            })
          );
        } catch {}
      }
    }

    let appliedPatchVersion = particlesPatchVersion;
    function applyLatestPerScopePatchesIfAny() {
      if (appliedPatchVersion === particlesPatchVersion) return;
      if (particlesPatchStore.parent)
        mergeIntoDeep(parentES, particlesPatchStore.parent);
      sub.applyLatestPatches(particlesPatchStore, layers);
      gravityAmbientActive = anyGravityEnabled();
      appliedPatchVersion = particlesPatchVersion;
    }

    let seenFireCounter = fireCounter;
    let acceptExternalTransform = true;
    let remainS = Infinity;

    let parentLivePrev = false;
    let parentSpawnPrev = false;
    let parentMarkedFinished = false;
    let parentStartedAtMs = 0;
    let parentSpawnEnabled = false;
    let parentOriginAtStart = parentEmitter.getOrigin
      ? parentEmitter.getOrigin()
      : { x: imageRect.width * 0.5, y: imageRect.height * 0.5 };

    const DEAD_CONFIRM_FRAMES = 2;
    let parentDeadStableFrames = 0;

    function markSequenceStart(originNow) {
      particlesEmptySent = false;
      parentMarkedFinished = false;
      parentDeadStableFrames = 0;
      parentStartedAtMs = nowMs();
      parentSpawnEnabled = true;
      parentEmitter.setSpawning(true);
      try { sub.setSpawningChildren(true); } catch {}
      sub.armChildrenStart(originNow, parentStartedAtMs);
      parentLivePrev = !!parentEmitter.hasLiveParticles?.();
      parentSpawnPrev = !!parentEmitter.isSpawning?.();
      dispatchRuntimeLastOrigin("start", originNow);
      if (refStepId != null) {
        try {
          window.dispatchEvent(
            new CustomEvent("EmitterTool:Sequencer:StepStarted", {
              detail: {
                refStepId,
                origin: originNow,
                pxPerMeter: pxPerMeterVal,
                imageRect,
              },
            })
          );
        } catch {}
      }
    }

    function stopParentSpawningIfNeeded() {
      parentEmitter.setSpawning(false);
      parentSpawnEnabled = false;
    }

    function checkParentFinishAndMaybeArmChildren() {
      const liveNow = !!parentEmitter.hasLiveParticles?.();
      const spawnNow = !!parentEmitter.isSpawning?.();

      if (!spawnNow && !liveNow) {
        parentDeadStableFrames++;
      } else {
        parentDeadStableFrames = 0;
      }

      const parentFullyDeadStable = parentDeadStableFrames >= DEAD_CONFIRM_FRAMES;

      if (!parentMarkedFinished && parentFullyDeadStable) {
        parentMarkedFinished = true;
        const finalPos =
          lastDeathPos &&
          Number.isFinite(lastDeathPos?.x) &&
          Number.isFinite(lastDeathPos?.y)
            ? lastDeathPos
            : parentEmitter.getOrigin
            ? parentEmitter.getOrigin()
            : parentOriginAtStart;

        sub.armAfterParent(finalPos, nowMs());
        dispatchRuntimeLastOrigin("parent-finished", finalPos);
      }

      parentLivePrev = liveNow;
      parentSpawnPrev = spawnNow;

      return parentFullyDeadStable;
    }

    function fullSequenceFinished() {
      const parentFullyDead =
        !parentEmitter.isSpawning?.() && !parentEmitter.hasLiveParticles?.();
      const kidsDone = sub.areChildrenAllFinished();
      return parentFullyDead && kidsDone;
    }

    let modeNow = "infinite";
    if (usePresetRulesOnly && fixedMode) {
      modeNow = fixedMode;
    } else {
      try {
        modeNow = (localStorage.getItem(UI_FIRE_MODE_KEY) || "infinite").toLowerCase();
      } catch {}
    }
    currentModeCache = modeNow;
    if (modeNow === "off") {
      if (!appendMode) clearAllInstances();
      return;
    }

    if (!atlas || atlas._stamp !== texturesRef) {
      atlas = {
        textures: texturesRef || {},
        frameNames: Object.keys(texturesRef || {}),
        spritesheet: null,
      };
      atlas._stamp = texturesRef;
    }

    const instance = {
      update(dt) {
        if (!usePresetRulesOnly) {
          _glowWireCooldown -= dt;
          if (_glowWireCooldown <= 0) {
            _glowWireCooldown = 0.5;
            try {
              glowReg.wireNow();
            } catch {}
          }
        }

        applyLatestPerScopePatchesIfAny();

        for (const [cid, rule] of subRuleOverrides) {
          try { sub.applyChildRuleOverride(cid, rule); } catch {}
        }

        // Helper to pass the parent’s live origin to children each frame (so delayed ones track it)
        const parentOriginNow = parentEmitter.getOrigin ? parentEmitter.getOrigin() : null;

        if (forceFadeActive) {
          parentEmitter.update(dt);

          sub.tickChildren(
            dt,
            nowMs(),
            !!parentEmitter.hasLiveParticles?.(),
            !!parentEmitter.isSpawning?.(),
            resolvePxPerMeter,
            applyOrbitToEmitter,
            applyRotationToEmitter,
            PIXI_,
            parentOriginNow
          );

          applyOrbitToEmitter(
            parentEmitter,
            parentES,
            resolvePxPerMeter(80),
            dt,
            PIXI_
          );

          const hpFade =
            parentEmitter.hasLiveParticles && parentEmitter.hasLiveParticles()
              ? parentEmitter.getHeadPosition
                ? parentEmitter.getHeadPosition()
                : null
              : null;
          trail.update(dt, hpFade);

          const total = forceFadeTotalS > 0 ? forceFadeTotalS : 0.25;
          forceFadeRemainS -= dt;
          const tNorm = total > 0 ? Math.max(0, Math.min(1, forceFadeRemainS / total)) : 0;
          const alpha = tNorm;
          for (const g of emitterGroups) {
            try {
              g.alpha = alpha;
            } catch {}
          }

          if (forceFadeRemainS <= 0) {
            for (const g of emitterGroups) {
              try {
                g.alpha = 0;
              } catch {}
            }
            dispatchRuntimeLastOrigin("fade-complete");
            dispatchParticlesEmpty("fade-complete");
            resetAllEmitters({ clearParticles: true });
            timedArmed = false;
            phaseActive = false;
            forceFadeActive = false;
            return false;
          }

          return true;
        }

        let modeLS = "infinite";
        if (usePresetRulesOnly && fixedMode) {
          modeLS = fixedMode;
        } else {
          try {
            modeLS = (localStorage.getItem(UI_FIRE_MODE_KEY) || "infinite").toLowerCase();
          } catch {}
        }

        if (!usePresetRulesOnly) {
          try {
            const styleLS = (
              localStorage.getItem(UI_RULE_STYLE_KEY) || ruleStyle
            ).toLowerCase();
            const durLS = toNum(
              localStorage.getItem(UI_RULE_DUR_KEY),
              ruleDurationS
            );
            ruleStyle = styleLS === "timed" ? "timed" : "infinite";
            ruleDurationS = Math.max(0, durLS);
          } catch {}
        }

        if (modeLS === "off") return false;

        if (modeLS === "timed") {
          if (!timedArmed) {
            timedArmed = true;

            let originNow =
              (parentEmitter.getOrigin && parentEmitter.getOrigin()) || {
                x: imageRect.width * 0.5,
                y: imageRect.height * 0.5,
              };

            if (gravityAmbientActive) {
              originNow = gravityLockedOrigin(
                parent.emitterShape,
                imageRect,
                originNow
              );
            }

            setOriginAll(originNow);
            configureGravityAll(originNow);

            let dur = 0;
            if (!usePresetRulesOnly) {
              try {
                dur = Number(localStorage.getItem(UI_TIMED_DUR_KEY));
              } catch {}
            }
            if (!Number.isFinite(dur) || dur <= 0) dur = Number(ruleDurationS) || 0;
            if (!Number.isFinite(dur) || dur <= 0) dur = 2;
            timedRemainS = Math.max(0, dur);

            markSequenceStart(originNow);
          }

          timedRemainS -= dt;
          if (timedRemainS <= 0) {
            timedRemainS = 0;
            stopParentSpawningIfNeeded();
            startForcedFadeOut();
          }

          parentEmitter.update(dt);

          sub.tickChildren(
            dt,
            nowMs(),
            !!parentEmitter.hasLiveParticles?.(),
            !!parentEmitter.isSpawning?.(),
            resolvePxPerMeter,
            applyOrbitToEmitter,
            applyRotationToEmitter,
            PIXI_,
            parentOriginNow
          );

          applyOrbitToEmitter(
            parentEmitter,
            parentES,
            resolvePxPerMeter(80),
            dt,
            PIXI_
          );

          checkParentFinishAndMaybeArmChildren();

          const hp =
            parentEmitter.hasLiveParticles && parentEmitter.hasLiveParticles()
              ? parentEmitter.getHeadPosition
                ? parentEmitter.getHeadPosition()
                : null
              : null;
          trail.update(dt, hp);

          if (!anyLive() && fullSequenceFinished()) {
            dispatchRuntimeLastOrigin("timed-complete");
            dispatchParticlesEmpty("timed-complete");
            resetAllEmitters({ clearParticles: true });
            timedArmed = false;
            return false;
          }

          return true;
        }

        if (modeLS === "rule") {
          if (
            !phaseActive ||
            !sessionPick ||
            !Number.isFinite(sessionPick.devX) ||
            !Number.isFinite(sessionPick.devY)
          ) {
            stopParentSpawningIfNeeded();

            const originNow = gravityAmbientActive
              ? gravityLockedOrigin(
                  parent.emitterShape,
                  imageRect,
                  {
                    x: imageRect.width * 0.5,
                    y: imageRect.height * 0.5,
                  }
                )
              : {
                  x: imageRect.width * 0.5,
                  y: imageRect.height * 0.5,
                };

            configureGravityAll(originNow);

            parentEmitter.update(dt);

            sub.tickChildren(
              dt,
              nowMs(),
              !!parentEmitter.hasLiveParticles?.(),
              !!parentEmitter.isSpawning?.(),
              resolvePxPerMeter,
              applyOrbitToEmitter,
              applyRotationToEmitter,
              PIXI_,
              parentOriginNow
            );

            applyOrbitToEmitter(
              parentEmitter,
              parentES,
              resolvePxPerMeter(80),
              dt,
              PIXI_
            );

            const hp =
              parentEmitter.hasLiveParticles &&
              parentEmitter.hasLiveParticles()
                ? parentEmitter.getHeadPosition
                  ? parentEmitter.getHeadPosition()
                  : null
                : null;
            trail.update(dt, hp);

            return true;
          }

          if (
            !gravityAmbientActive &&
            Number.isFinite(sessionPick.angleDeg)
          ) {
            setRotationDegAll(sessionPick.angleDeg);
            // overlay omitted by ENGINE_OVERLAY_ENABLED==false
          } else if (
            !gravityAmbientActive &&
            Number.isFinite(sessionPick.targetDevX) &&
            Number.isFinite(sessionPick.targetDevY) &&
            Number.isFinite(sessionPick.xCss) &&
            Number.isFinite(sessionPick.yCss)
          ) {
            // compute bearing for overlay if needed
          }

          const isTokenToTarget =
            Number.isFinite(sessionPick.targetDevX) &&
            Number.isFinite(sessionPick.targetDevY);

          if (fireCounter !== seenFireCounter) {
            seenFireCounter = fireCounter;

            resetAllEmitters({ clearParticles: true });

            let originNow;
            if (gravityAmbientActive) {
              originNow = gravityLockedOrigin(
                parent.emitterShape,
                imageRect,
                { x: sessionPick.devX, y: sessionPick.devY }
              );
              moveHeadActive = false;
            } else if (isTokenToTarget) {
              let mps = Number(sessionPick.headSpeedMps);
              if (!Number.isFinite(mps)) {
                try {
                  const v = Number(localStorage.getItem(UI_TTT_SPEED_MPS));
                  if (Number.isFinite(v)) mps = v;
                } catch {}
              }
              if (!Number.isFinite(mps)) mps = 8;
              const speed = Math.max(0, mps) * resolvePxPerMeter(80);

              headPos = { x: sessionPick.devX, y: sessionPick.devY };
              headTarget = {
                x: sessionPick.targetDevX,
                y: sessionPick.targetDevY,
              };
              headSpeedPxPerS = speed;
              moveHeadActive = true;

              originNow = { x: headPos.x, y: headPos.y };

              if (!gravityAmbientActive && headPos && headTarget) {
                const dx0 = headTarget.x - headPos.x;
                const dy0 = headTarget.y - headPos.y;
                const ang0 = (Math.atan2(dy0, dx0) * 180) / Math.PI;
                setRotationDegAll(((ang0 % 360) + 360) % 360);
              }
            } else {
              originNow = { x: sessionPick.devX, y: sessionPick.devY };
              moveHeadActive = false;
            }

            setOriginAll(originNow);
            configureGravityAll(originNow);

            markSequenceStart(originNow);

            remainS =
              ruleStyle === "timed"
                ? Math.max(0, Number(ruleDurationS) || 0)
                : Infinity;
          }

          acceptExternalTransform = false;

          if (
            !gravityAmbientActive &&
            isTokenToTarget &&
            moveHeadActive &&
            headPos &&
            headTarget
          ) {
            const dx = headTarget.x - headPos.x;
            const dy = headTarget.y - headPos.y;
            const dist = Math.hypot(dx, dy);
            if (
              dist <=
                Math.max(1, resolvePxPerMeter(80) * 0.05) ||
              headSpeedPxPerS <= 0
            ) {
              moveHeadActive = false;
              stopParentSpawningIfNeeded();
              dispatchRuntimeLastOrigin("target-reached", headPos);
              try {
                window.dispatchEvent(
                  new CustomEvent("EmitterTool:TargetReached", {
                    detail: {
                      refStepId: refStepId ?? undefined,
                    },
                  })
                );
              } catch {}
            } else {
              const step = headSpeedPxPerS * Math.max(0, dt);
              const k = step >= dist ? 1 : step / dist;
              headPos = {
                x: headPos.x + dx * k,
                y: headPos.y + dy * k,
              };
              setOriginAll(headPos);
              configureGravityAll(headPos);
              const angMove =
                (Math.atan2(dy, dx) * 180) / Math.PI;
              setRotationDegAll(((angMove % 360) + 360) % 360);
            }
          }

          if (ruleStyle === "timed" && !moveHeadActive) {
            remainS -= dt;
            if (remainS <= 0) {
              remainS = 0;
              stopParentSpawningIfNeeded();
              startForcedFadeOut();
            }
          }

          parentEmitter.update(dt);

          sub.tickChildren(
            dt,
            nowMs(),
            !!parentEmitter.hasLiveParticles?.(),
            !!parentEmitter.isSpawning?.(),
            resolvePxPerMeter,
            applyOrbitToEmitter,
            applyRotationToEmitter,
            PIXI_,
            parentOriginNow
          );

          applyOrbitToEmitter(
            parentEmitter,
            parentES,
            resolvePxPerMeter(80),
            dt,
            PIXI_
          );

          const hpRule =
            parentEmitter.hasLiveParticles &&
            parentEmitter.hasLiveParticles()
              ? parentEmitter.getHeadPosition
                ? parentEmitter.getHeadPosition()
                : null
              : null;
          trail.update(dt, hpRule);

          const stillSpawningAny = emittersAll.some(
            (e) => e.isSpawning && e.isSpawning()
          );
          if (!stillSpawningAny && fullSequenceFinished()) {
            phaseActive = false;
            try {
              window.dispatchEvent(
                new CustomEvent("EmitterTool:RulePhaseEnd", {
                  detail: {},
                })
              );
            } catch {}
            dispatchRuntimeLastOrigin("rule-complete");
            dispatchParticlesEmpty("rule-complete");
            resetAllEmitters({ clearParticles: true });
            acceptExternalTransform = true;
          }

          checkParentFinishAndMaybeArmChildren();

          return true;
        }

        {
          let o = { x: imageRect.width * 0.5, y: imageRect.height * 0.5 };
          const h =
            typeof helpers?.getOrigin === "function"
              ? helpers.getOrigin({ imageRect })
              : null;
          if (h && Number.isFinite(h.x) && Number.isFinite(h.y)) {
            o = { x: h.x, y: h.y };
          }
          if (gravityAmbientActive) {
            o = gravityLockedOrigin(parent.emitterShape, imageRect, o);
          }

          setOriginAll(o);
          configureGravityAll(o);

          if (!parentSpawnEnabled) {
            markSequenceStart(o);
          }

          parentEmitter.update(dt);

          sub.tickChildren(
            dt,
            nowMs(),
            !!parentEmitter.hasLiveParticles?.(),
            !!parentEmitter.isSpawning?.(),
            resolvePxPerMeter,
            applyOrbitToEmitter,
            applyRotationToEmitter,
            PIXI_,
            parentOriginNow
          );

          applyOrbitToEmitter(
            parentEmitter,
            parentES,
            resolvePxPerMeter(80),
            dt,
            PIXI_
          );

          checkParentFinishAndMaybeArmChildren();

          const hpInf =
            parentEmitter.hasLiveParticles &&
            parentEmitter.hasLiveParticles()
              ? parentEmitter.getHeadPosition
                ? parentEmitter.getHeadPosition()
                : null
              : null;
          trail.update(dt, hpInf);

          return true;
        }
      },

      destroy() {
        try {
          parentEmitter.destroy?.();
        } catch {}
        try {
          trail.destroy?.();
        } catch {}
        sub.destroy();
        for (const g of emitterGroups) {
          try {
            g.removeChildren();
          } catch {}
          try {
            g.parent && g.parent.removeChild(g);
          } catch {}
          try {
            g.destroy({ children: true });
          } catch {}
        }
      },

      _ctx: {
        PIXI: PIXI_,
        atlas,
        pxPerMeter: resolvePxPerMeter(80),
        effectKey: "template",
      },
    };

    if (!appendMode) clearAllInstances();
    live.add(instance);

    try {
      instance.update(1 / 60);
      instance.update(1 / 60);
      instance.update(1 / 60);
    } catch {}
  }

  function update(dt) {
    const dead = [];
    for (const inst of live) {
      try {
        const keep =
          typeof inst.update === "function"
            ? inst.update(dt) !== false
            : !inst.done;
        if (!keep) dead.push(inst);
      } catch {
        dead.push(inst);
      }
    }
    for (const d of dead) {
      try {
        d.destroy?.();
      } catch {}
      live.delete(d);
    }
  }

  function destroy() {
    clearAllInstances();
    atlas = null;

    try {
      window.removeEventListener("EmitterTool:RuleConfig", onRuleCfg);
    } catch {}
    try {
      window.removeEventListener("EmitterTool:ParticlesConfig", onParticlesCfg);
    } catch {}
    try {
      window.removeEventListener("EmitterTool:UI:TargetingPreset", onUIPreset);
    } catch {}
    try {
      window.removeEventListener("EmitterTool:Targeting:SetRangeM", onUIRange);
    } catch {}

    if (appRef && autorun && createUnifiedEmitterRuntime._tick) {
      try {
        (appRef.ticker || PIXI.Ticker.shared).remove(
          createUnifiedEmitterRuntime._tick
        );
      } catch {}
    }
  }

  function setEnvironment(env = {}) {
    const { pxPerMeter: ppm, ...rest } = env || {};
    if (Number.isFinite(ppm)) arg.pxPerMeter = Number(ppm);

    glowEnv = { ...glowEnv, ...rest };

    if (!usePresetRulesOnly) {
      try {
        if (glowReg && typeof glowReg.setEnvironment === "function") {
          glowReg.setEnvironment({
            pxPerMeter: resolvePxPerMeter(80),
            ...glowEnv,
          });
        }
      } catch {}
    }
  }

  if (appRef && autorun) {
    const tick = (dtPixi) => update((dtPixi || 1) / 60);
    createUnifiedEmitterRuntime._tick = tick;
    (appRef.ticker || PIXI.Ticker.shared).add(tick);
  }

  return {
    runPreset: runPreset,
    update: update,
    destroy: destroy,
    setEnvironment: setEnvironment,
    setTextures: setTextures,
    setSpriteManager: setSpriteManager,
    setExternalTransform: () => {},
    setParentShapeSettings: () => {},
  };
}

export default { createUnifiedEmitterRuntime };
